import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, ButtonToolbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { ActiveFilterViewer, Filters } from '@rankmi/rankmi-table'

import { CourseStateType } from 'enum'
import { IMainState } from 'store/states'
import { ICoursesState } from 'store/states/courses.state'
import * as fromActions from 'store/actions/courses.action'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import { generateFilterViewerLocale } from 'helpers/translations.helper'
import { PathRoutes } from 'enum/routes'
import { useAccess } from 'contexts/AccessContext'
import CourseList from './components/courseList'
import { Actions } from './components/Actions'

const Index: React.FC = () => {
  let bounceId: any
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { download } = useSelector(
    ({ courses }: IMainState) => courses as ICoursesState
  )
  const [searchText, setSearchText] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({} as any)
  const [filtersColumns, setFilterColumns] = useState([] as any)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState({})
  const [perPage, setPerPage] = useState(50)
  const [selectedRows, setSelectedRows] = useState([] as any)
  const [fastFilters, setFastFilters] = useState(0 as any)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  const prefix = 'administration.courses.list.create.table.headers'
  const searchTextRef = useRef<any>()
  const filtersRef = useRef()
  const { allProfilesList, loading: profilesLoading } = useSelector(
    ({ lmsProfiles }: IMainState) => lmsProfiles
  )
  const [translations, setTranslations] = useState<any>()
  useEffect(() => {
    setTranslations(() => generateFilterViewerLocale())
  }, [])
  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  useEffect(() => {
    //  setLoading(true)
    searchTextRef.current = searchText
    filtersRef.current = selectedFilters

    const timer = setTimeout(() => {
      dispatch({
        type: fromActions.COURSES_ADMINISTRATION_GET_LIST,
        payload: {
          page,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        },
      })
    }, 1000)
    return () => clearTimeout(timer)
  }, [page, sort, searchText, perPage, selectedFilters, fastFilters, dispatch])

  useEffect(() => {
    if (!profilesLoading) {
      setFilterColumns([
        {
          label: t(`${prefix}.name`),
          value: 'search_name',
          type: 'string',
        },
        {
          label: t(`${prefix}.forMandatoryProfiles`),
          value: 'required_profiles_id',
          type: 'tagsList',
          list: allProfilesList.map(
            ({ id: value, name: label, color }: any) => ({
              value,
              label,
              color,
            })
          ),
        },
        {
          label: t(`${prefix}.forProfiles`),
          value: 'optional_profiles_id',
          type: 'tagsList',
          list: allProfilesList.map(
            ({ id: value, name: label, color }: any) => ({
              value,
              label,
              color,
            })
          ),
        },
        {
          label: t(`${prefix}.createAt`),
          value: 'created_at',
          type: 'date',
        },
        {
          label: t(`${prefix}.state`),
          value: 'state',
          type: 'list',
          list: [
            CourseStateType.Draft,
            CourseStateType.Published,
            CourseStateType.Imparted,
            CourseStateType.Soon,
          ].map((state) => ({
            label: t(`commons.course.state.${CourseStateType[state]}`),
            value: state.toString(),
          })),
        },
        {
          label: t(`${prefix}.courseType.synchronousCourse`),
          value: 'synchronous',
          type: 'boolean',
        },
      ])
    }
  }, [allProfilesList, profilesLoading, t])

  const {
    loading,
    modules: { administration },
    permissions: { canManageCourses },
  } = useAccess()

  useEffect(() => {
    if (!loading && !(administration || canManageCourses))
      history.push(PathRoutes.MAIN)
  }, [loading, administration, canManageCourses, history])

  const handleSearch = (value: string) => {
    if (bounceId) {
      clearTimeout(bounceId)
    }
    bounceId = setTimeout(() => {
      setSearchText(value.trim())
    }, 1000)
  }

  const handleDelete = (id: number) => {
    dispatch({
      type: fromActions.DESTROY_COURSE,
      payload: {
        id,
        params: {
          page,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        },
      },
    })
  }

  useEffect(() => {
    download !== '' && dispatch({ type: fromActions.CLEAR_COURSES_DOWNLOAD })
  }, [dispatch, download])

  return (
    <>
      <Row className={'d-flex align-items-center mb-3'}>
        <Col>
          <h1>{t('administration.courses.list.managementTitle')}</h1>
        </Col>
      </Row>
      <Row>
        <Col>
          <ButtonToolbar className="float-left">
            <Filters
              columns={filtersColumns}
              onChange={(data: any) => setSelectedFilters(data)}
              open={showFiltersModal}
              onVisibilityChange={(state: boolean) =>
                setShowFiltersModal(state)
              }
              locale={translations}
            />
          </ButtonToolbar>
          <Actions
            {...{
              allSelected,
              selectedRows,
              searchText,
              selectedFilters,
              fastFilters,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <ActiveFilterViewer
            activeFilters={selectedFilters}
            onChange={(data: any) => setSelectedFilters(data)}
            openFilters={(state: boolean) => setShowFiltersModal(state)}
            columns={filtersColumns}
            locale={translations}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CourseList
            onDelete={handleDelete}
            setSearch={handleSearch}
            setPage={setPage}
            setSort={setSort}
            setPerPage={setPerPage}
            setSelectedRows={setSelectedRows}
            setFastFilters={setFastFilters}
            setAllSelected={setAllSelected}
          />
        </Col>
      </Row>
    </>
  )
}

export default Index
